import { Box, Group, Text } from "@mantine/core";
import { IconGift } from "@tabler/icons-react";
import { BookedService } from "../../../../../types/transactions";
import { ServicePriceBasis } from "../../../../../types/context";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import {
  formatQty,
  isPerUnitBasis,
  unitLabelForBasis,
} from "../../../../../utils/quantityUtils";
import styles from "./RedemptionBreakdown.module.css";

interface RedemptionBreakdownProps {
  service: BookedService;
  /**
   * The service's price basis (per_unit / per_syringe / per_service), looked up
   * from the salon catalog by the caller. Drives the per-unit vs per-service
   * layout; treated as per_service when omitted.
   */
  priceBasis?: ServicePriceBasis | string | null;
  /**
   * Which pane this renders in:
   * - "summary" (right Appointment Summary / checkout summary): per-unit lines
   *   show a "total / redeemed / remaining × unit price" breakdown; per-service
   *   lines show "N of M redeemed", the covered amount, and the net.
   * - "detail" (left editable Transaction Items list): per-service lines show
   *   the struck full price → net in the row's price column, so here they only
   *   need the redeemed-count context; per-unit lines show the redeemed count
   *   plus the net.
   */
  variant?: "summary" | "detail";
}

/**
 * Per-line package-redemption breakdown shown under a covered service in both
 * the editable Transaction Items list and the read-only Appointment Summary.
 * All money figures exclude tax/tip (redeemed units are settled at their list
 * unit price; the covered portion carries no tax at checkout). Renders nothing
 * when the line has no redemptions.
 */
export default function RedemptionBreakdown({
  service,
  priceBasis,
  variant = "summary",
}: RedemptionBreakdownProps) {
  const unitsRedeemed = service.calc__units_redeemed ?? 0;
  if (unitsRedeemed <= 0) return null;

  const totalUnits = service.quantity ?? 1;
  const unitsRemaining = Math.max(0, totalUnits - unitsRedeemed);

  // Per-unit / per-syringe lines: a units-level breakdown.
  if (isPerUnitBasis(priceBasis)) {
    return (
      <Box className={styles.redemption}>
        <Group gap={6} wrap="nowrap" className={styles.line}>
          <IconGift size={13} stroke={1.8} />
          <Text component="span" size="xs">
            {variant === "summary"
              ? `${formatQty(unitsRedeemed)} redeemed, ${formatQty(
                  unitsRemaining,
                )} remaining`
              : `${formatQty(unitsRedeemed)} ${unitLabelForBasis(
                  priceBasis,
                  unitsRedeemed,
                )} redeemed`}
          </Text>
        </Group>
      </Box>
    );
  }

  // Per-service detail pane: the struck full price → net lives in the row's
  // price column, so the breakdown only supplies the redeemed-count context.
  if (variant === "detail") {
    return (
      <Box className={styles.redemption}>
        <Group gap={6} wrap="nowrap" className={styles.line}>
          <IconGift size={13} stroke={1.8} />
          <Text component="span" size="xs">
            Package covered {formatQty(unitsRedeemed)} of{" "}
            {formatQty(totalUnits)}
          </Text>
        </Group>
      </Box>
    );
  }

  // Per-service summary pane: "N of M redeemed", the covered amount, and net.
  return (
    <Box className={styles.redemption}>
      <Group gap={6} wrap="nowrap" className={styles.line}>
        <IconGift size={13} stroke={1.8} />
        <Text component="span" size="xs">
          {formatQty(unitsRedeemed)} of {formatQty(totalUnits)} redeemed
        </Text>
      </Group>
    </Box>
  );
}
